import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useSearch } from 'Components/Organisms/SearchProvider';
import {
    updateTransaction,
    associateTagToTransactions,
    dissociateTagToTransactions,
    getTransaction,
    associateContactToTransactions,
    dissociateContactToTransactions,
    reconcileTransaction
} from 'Services/transactions';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { trackError } from 'Utils/errorMonitoring';

export default function useTransaction(id, options = {}) {
    const { searchParams } = useSearch();
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();

    const { data: transaction = {}, isLoading } = useQuery({
        queryKey: ['transactions', id],
        queryFn: async () => await getTransaction(id),
        onError: (error) => {
            if (error.response?.status === 404) return;

            trackError(error);
        },
        enabled: !!id,
        ...options,
    });

    const { mutate: associateMultipleTags } = useMutation({
        mutationFn: async (tagIds) => await Promise.all(tagIds.map((tagId) => associateTagToTransactions(tagId, [id]))),
        onMutate: (tagIds) => {
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    tags_ids: [...old.tags_ids, ...tagIds],
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        return {
                            ...page,
                            items: page.items.map((transaction) => {
                                if (transaction.id === id) {
                                    return {
                                        ...transaction,
                                        tags_ids: [...transaction.tags_ids, ...tagIds],
                                    };
                                }
                                return transaction;
                            }),
                        };
                    }),
                };
            });
            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: associateTag } = useMutation({
        mutationFn: async (tagId) => await associateTagToTransactions(tagId, [id]),
        onMutate: (tagId) => {
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    tags_ids: [...old.tags_ids, tagId],
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        return {
                            ...page,
                            items: page.items.map((transaction) => {
                                if (transaction.id === id) {
                                    return {
                                        ...transaction,
                                        tags_ids: [...transaction.tags_ids, tagId],
                                    };
                                }
                                return transaction;
                            }),
                        };
                    }),
                };
            });
            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: dissociateMultipleTags } = useMutation({
        mutationFn: async (tagIds) =>
            await Promise.all(tagIds.map((tagId) => dissociateTagToTransactions(tagId, [id]))),
        onMutate: (tagIds) => {
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    tags_ids: old.tags_ids.filter((tId) => !tagIds.includes(tId)),
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        let count = page.count;
                        const items = page.items
                            .filter((transaction) => {
                                if (transaction.id !== id) return true;

                                if (!Array.isArray(searchParams.tags_ids)) return true;

                                const isFiltered = !searchParams.tags_ids
                                    .filter((tagIdParam) => !tagIds.includes(tagIdParam))
                                    .some((tagIdParam) => transaction.tags_ids.includes(tagIdParam));

                                if (isFiltered) {
                                    count--;
                                    return false;
                                }
                                return true;
                            })
                            .map((transaction) => {
                                if (transaction.id === id) {
                                    return {
                                        ...transaction,
                                        tags_ids: transaction.tags_ids.filter((id) => !tagIds.includes(id)),
                                    };
                                }
                                return transaction;
                            });

                        return { ...page, items, count };
                    }),
                };
            });
            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: dissociateTag } = useMutation({
        mutationFn: async (tagId) => await dissociateTagToTransactions(tagId, [id]),
        onMutate: (tagId) => {
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    tags_ids: old.tags_ids.filter((tId) => tId !== tagId),
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        let count = page.count;
                        const items = page.items
                            .filter((transaction) => {
                                if (transaction.id !== id) return true;

                                if (!Array.isArray(searchParams.tags_ids)) return true;

                                const isFiltered = !searchParams.tags_ids
                                    .filter((tagIdParam) => tagIdParam !== tagId)
                                    .some((tagIdParam) => transaction.tags_ids.includes(tagIdParam));

                                if (isFiltered) {
                                    count--;
                                    return false;
                                }
                                return true;
                            })
                            .map((transaction) => {
                                if (transaction.id === id) {
                                    return {
                                        ...transaction,
                                        tags_ids: transaction.tags_ids.filter((id) => id !== tagId),
                                    };
                                }
                                return transaction;
                            });

                        return { ...page, items, count };
                    }),
                };
            });
            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateTransactionDescription } = useMutation({
        mutationFn: async (description) => await updateTransaction(id, { description }),
        onMutate: async (description) => {
            await queryClient.cancelQueries({ queryKey: ['transactions'] });
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    description,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    description,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });
            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateTransactionComment } = useMutation({
        mutationFn: async (comment) => await updateTransaction(id, { comment }),
        onMutate: async (comment) => {
            await queryClient.cancelQueries({ queryKey: ['transactions', id] });

            const previousTransaction = queryClient.getQueryData(['transactions', id]);
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    comment,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    comment: comment === null ? '' : comment,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }

            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateTransactionExclude } = useMutation({
        mutationFn: async (isExclude) => await updateTransaction(id, { exclude_accounting: isExclude }),
        onMutate: async (isExclude) => {
            await queryClient.cancelQueries({ queryKey: ['transactions'] });
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    exclude_accounting: isExclude,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    exclude_accounting: isExclude,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateReverseSign } = useMutation({
        mutationFn: async (isReverseSign) => await updateTransaction(id, { reverse_sign: isReverseSign }),
        onMutate: async (isReverseSign) => {
            await queryClient.cancelQueries({ queryKey: ['transactions'] });
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    reverse_sign: isReverseSign,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    reverse_sign: isReverseSign,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateTransactionDate } = useMutation({
        mutationFn: async (date_booked) => await updateTransaction(id, { date_booked }),
        onMutate: async (date_booked) => {
            await queryClient.cancelQueries({ queryKey: ['transactions'] });
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            const dateBooked = new Date(date_booked);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    date_booked: dateBooked,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    date_booked: dateBooked,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: associateContact } = useMutation({
        mutationFn: async (contactId) => await associateContactToTransactions(contactId, [id]),
        onMutate: async (contactId) => {
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    contact_id: contactId,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    contact_id: contactId,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: dissociateContact } = useMutation({
        mutationFn: async () => await dissociateContactToTransactions([id]),
        onMutate: async () => {
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    contact_id: null,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    contact_id: null,
                                };
                            }
                            return transaction;
                        });

                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: updateTransactionMutation, isLoading: isUpdating } = useMutation({
        mutationFn: async (data) => await updateTransaction(id, data),
        onMutate: async (data) => {
            await queryClient.cancelQueries({ queryKey: ['transactions', id] });
            const previousTransaction = queryClient.getQueryData(['transactions', id]);
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);

            const normalizeData = (data) => {
                return Object.fromEntries(
                    Object.entries(data).map(([key, value]) => {
                        const normalizedKey = key === 'taxonomies_tags_ids' ? 'tags_ids' : key;
                        const noralizedValue = key === 'date_booked' ? new Date(value) : value;

                        return [normalizedKey, noralizedValue];
                    }),
                );
            };

            const normalizedData = normalizeData(data);

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    ...normalizedData,
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    ...normalizedData,
                                };
                            }
                            return transaction;
                        });
                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.resetQueries({ queryKey: ['dashboard'] });
            queryClient.resetQueries({ queryKey: ['forecasts'] });
        },
    });

    const { mutate: reconcile, isLoading: isReconciling } = useMutation({
        mutationFn: async ({ invoicesIds, forceReconciliationStatus = null }) =>
            await reconcileTransaction(id, { invoicesIds, forceReconciliationStatus }),
        onMutate: async ({ invoicesIds, forceReconciliationStatus }) => {
            await queryClient.cancelQueries({ queryKey: ['transactions'] });
            const previousTransactions = queryClient.getQueryData(['transactions', searchParams]);
            const previousTransaction = queryClient.getQueryData(['transactions', id]);

            const getIsReconciled = (currentIsReconciled) => {
                if (forceReconciliationStatus === true) return true;
                if (forceReconciliationStatus === false) return false;
                return currentIsReconciled;
            };

            queryClient.setQueryData(['transactions', id], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    invoices_ids: invoicesIds,
                    is_reconciled: getIsReconciled(old.is_reconciled),
                };
            });

            queryClient.setQueryData(['transactions', searchParams], (old) => {
                if (!old) return old;

                return {
                    ...old,
                    pages: old.pages.map((page) => {
                        const items = page.items.map((transaction) => {
                            if (transaction.id === id) {
                                return {
                                    ...transaction,
                                    invoices_ids: invoicesIds,
                                    is_reconciled: getIsReconciled(transaction.is_reconciled),
                                };
                            }
                            return transaction;
                        });
                        return { ...page, items };
                    }),
                };
            });

            return { previousTransactions, previousTransaction };
        },
        onError: (error, _variables, context) => {
            showUnknownError(error);
            if (context.previousTransaction) {
                queryClient.setQueryData(['transactions', id], context.previousTransaction);
            }
            if (context.previousTransactions) {
                queryClient.setQueryData(['transactions', searchParams], context.previousTransactions);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            queryClient.invalidateQueries({ queryKey: ['forecasts'] });
        },
    });

    return {
        isLoading,
        transaction,
        associateTag,
        dissociateTag,
        associateMultipleTags,
        dissociateMultipleTags,
        updateTransactionDescription,
        updateTransactionComment,
        updateTransactionExclude,
        updateReverseSign,
        updateTransactionDate,
        associateContact,
        dissociateContact,
        updateTransaction: updateTransactionMutation,
        isUpdating,
        reconcile,
        isReconciling
    };
}
